import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getTarotImage = (name: string) => {
  if (!name) return '';
  
  // Strip numbers and dots from the start (e.g. "0. The Fool" -> "The Fool") and trim
  const cleanName = name.replace(/^\d+\.\s*/, '').trim();

  const majorArcanaMap: { [key: string]: string } = {
    "The Fool": "00_the_fool.png",
    "The Magician": "01_the_magician.jpg",
    "The High Priestess": "02_the_high_priestess.png",
    "The Empress": "03_the_empress.jpg",
    "The Emperor": "04_the_emperor.jpg",
    "The Hierophant": "05_the_hierophant.jpg",
    "The Lovers": "06_the_lovers.jpg",
    "The Chariot": "07_the_chariot.jpg",
    "Strength": "08_strength.jpg",
    "The Hermit": "09_the_hermit.jpg",
    "Wheel of Fortune": "10_wheel_of_fortune.jpg",
    "Justice": "11_justice.jpg",
    "The Hanged Man": "12_the_hanged_man.jpg",
    "Death": "13_death.jpg",
    "Temperance": "14_temperance.jpg",
    "The Devil": "15_the_devil.jpg",
    "The Tower": "16_the_tower.jpg",
    "The Star": "17_the_star.jpg",
    "The Moon": "18_the_moon.jpg",
    "The Sun": "19_the_sun.jpg",
    "Judgement": "20_judgement.jpg",
    "The World": "21_the_world.jpg"
  };

  // Check Major Arcana first (case-insensitive check)
  const majorKey = Object.keys(majorArcanaMap).find(k => k.toLowerCase() === cleanName.toLowerCase());
  if (majorKey) {
    return `${API_URL}/tarot_images/${majorArcanaMap[majorKey]}`;
  }

  // Minor Arcana (e.g., "Ace of Wands")
  // Use case-insensitive split for " of "
  const minorParts = cleanName.split(/ of /i);
  if (minorParts.length === 2) {
    const rank = minorParts[0].toLowerCase().trim();
    const suit = minorParts[1].toLowerCase().trim();
    
    const rankMap: { [key: string]: string } = {
      'ace': 'ace',
      'one': 'ace',
      'two': '02',
      '2': '02',
      'three': '03',
      '3': '03',
      'four': '04',
      '4': '04',
      'five': '05',
      '5': '05',
      'six': '06',
      '6': '06',
      'seven': '07',
      '7': '07',
      'eight': '08',
      '8': '08',
      'nine': '09',
      '9': '09',
      'ten': '10',
      '10': '10',
      'page': 'page',
      'knight': 'knight',
      'queen': 'queen',
      'king': 'king'
    };

    const finalRank = rankMap[rank] || rank;
    return `${API_URL}/tarot_images/${suit}_${finalRank}.jpg`;
  }

  return '';
};

interface TarotCard {
  id: number;
  name: string;
  description: string;
  properties: string;
  long_description: string;
}

interface SelectedCard {
  instanceId: string; // Unique ID for this instance in the spread
  card: TarotCard;
  isReversed: boolean;
}

const TarotReader = ({ user }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tarotCards, setTarotCards] = useState<TarotCard[]>([]);
  const [selectedCards, setSelectedCards] = useState<SelectedCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [showRandomModal, setShowRandomModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [randomOptions, setRandomOptions] = useState({
    count: -1,
    type: 'both' // 'both', 'major', 'minor'
  });

  const sortTarotCards = (cards: TarotCard[]) => {
    const majorArcanaOrder = [
      "The Fool", "The Magician", "The High Priestess", "The Empress", "The Emperor",
      "The Hierophant", "The Lovers", "The Chariot", "Strength", "The Hermit",
      "Wheel of Fortune", "Justice", "The Hanged Man", "Death", "Temperance",
      "The Devil", "The Tower", "The Star", "The Moon", "The Sun",
      "Judgement", "The World"
    ];

    const suitOrder = ["wands", "pentacles", "cups", "swords"];
    const valueOrder = [
      "ace", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
      "page", "knight", "queen", "king"
    ];

    return [...cards].sort((a, b) => {
      const getSortValue = (card: TarotCard) => {
        const cleanName = card.name.replace(/^\d+\.\s*/, '').trim().toLowerCase();
        // Major Arcana
        const majorIndex = majorArcanaOrder.findIndex(k => k.toLowerCase() === cleanName);
        if (majorIndex !== -1) return majorIndex;

        // Minor Arcana
        const parts = cleanName.split(' of ');
        if (parts.length === 2) {
          const value = parts[0];
          const suit = parts[1];
          const suitIdx = suitOrder.indexOf(suit);
          const valueIdx = valueOrder.indexOf(value);
          
          if (suitIdx !== -1 && valueIdx !== -1) {
            // Base value for minor arcana starts after major arcana (22)
            // Each suit gets a block of 100 to keep them separate
            return 100 + (suitIdx * 100) + valueIdx;
          }
        }
        return 999; // Fallback for unknown cards
      };

      return getSortValue(a) - getSortValue(b);
    });
  };

  useEffect(() => {
    const fetchTarot = async () => {
      try {
        const res = await axios.get(`${API_URL}/references?category=tarot`, {
            headers: user ? { Authorization: `Bearer ${user.token}` } : {}
        });
        const allCards = sortTarotCards(res.data);
        setTarotCards(allCards);

        // Check for spread in URL
        const params = new URLSearchParams(location.search);
        const spreadParam = params.get('spread');
        if (spreadParam) {
          const cardEntries = spreadParam.split(',');
          const reconstructed: SelectedCard[] = cardEntries.map(entry => {
            const [id, rev] = entry.split(':');
            const card = allCards.find((c: any) => c.id === parseInt(id));
            return {
              instanceId: Math.random().toString(36).substr(2, 9),
              card,
              isReversed: rev === '1'
            };
          }).filter(item => item.card !== undefined) as SelectedCard[];
          setSelectedCards(reconstructed);
        }
      } catch (err) {
        console.error('Failed to fetch tarot cards', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTarot();
  }, [location.search, user]);

  const addCard = (card: TarotCard) => {
    const newSelected: SelectedCard = {
      instanceId: Math.random().toString(36).substr(2, 9),
      card,
      isReversed: false
    };
    setSelectedCards([...selectedCards, newSelected]);
    setShowPicker(false);
    setSearchTerm('');
  };

  const drawRandom = () => {
    let pool = [...tarotCards];
    if (randomOptions.type === 'major') {
      pool = pool.filter(c => !c.name.includes(' of '));
    } else if (randomOptions.type === 'minor') {
      pool = pool.filter(c => c.name.includes(' of '));
    }

    if (pool.length === 0) {
      alert("No cards found for this criteria.");
      return;
    }

    const newDraws: SelectedCard[] = [];
    const tempPool = [...pool];
    
    const countToDraw = randomOptions.count === -1 
      ? Math.floor(Math.random() * 4) + 1 
      : randomOptions.count;

    for (let i = 0; i < countToDraw; i++) {
      if (tempPool.length === 0) break;
      const randomIndex = Math.floor(Math.random() * tempPool.length);
      const card = tempPool.splice(randomIndex, 1)[0];
      newDraws.push({
        instanceId: Math.random().toString(36).substr(2, 9),
        card,
        isReversed: Math.random() > 0.5
      });
    }

    setSelectedCards(newDraws);
    setShowRandomModal(false);
  };

  const addToJournal = () => {
    const spreadString = selectedCards.map(s => `${s.card.id}:${s.isReversed ? '1' : '0'}`).join(',');
    const tarotLink = `${window.location.origin}/tarot-reader?spread=${spreadString}`;
    
    let htmlContent = `<h3>Tarot Reading - ${new Date().toLocaleDateString()}</h3>`;
    htmlContent += `<ul>`;
    selectedCards.forEach(s => {
      htmlContent += `<li><strong>${s.card.name}</strong> (${s.isReversed ? 'Reversed' : 'Upright'})</li>`;
    });
    htmlContent += `</ul>`;
    htmlContent += `<p><a href="${tarotLink}" style="color: var(--gold); text-decoration: underline;">✨ View this Tarot Spread in the Reader</a></p>`;
    htmlContent += `<p><em>My reflections on this reading:</em></p>`;

    navigate('/journal', { state: { prefillJournal: htmlContent } });
  };

  const removeCard = (instanceId: string) => {
    setSelectedCards(selectedCards.filter(c => c.instanceId !== instanceId));
  };

  const toggleOrientation = (instanceId: string) => {
    setSelectedCards(selectedCards.map(c => 
      c.instanceId === instanceId ? { ...c, isReversed: !c.isReversed } : c
    ));
  };

  const extractMeaning = (longDescription: string, isReversed: boolean, cardName: string) => {
    if (!longDescription) return "No detailed description available.";
    
    const sectionHeader = isReversed ? `## ${cardName} Reversed Meaning` : `## ${cardName} Upright Meaning`;
    const parts = longDescription.split(sectionHeader);
    
    if (parts.length < 2) {
      // Fallback if the header doesn't match exactly
      const altHeader = isReversed ? "## Reversed Meaning" : "## Upright Meaning";
      const altParts = longDescription.split(altHeader);
      if (altParts.length < 2) return longDescription; // Return full if not found
      return altParts[1].split('##')[0].trim();
    }
    
    // Get content after the header until the next header
    return parts[1].split('##')[0].trim();
  };

  const filteredCards = tarotCards.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.properties.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <LoadingSpinner message="Consulting the cards..." />;

  return (
    <div className="content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h2>Tarot Spread Reader</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {selectedCards.length > 0 && (
            <button onClick={addToJournal} style={{ backgroundColor: 'var(--mystic-purple)' }}>
               📔 Add to Journal
            </button>
          )}
          <button onClick={() => setShowRandomModal(true)} className="btn-gold">
            🎲 Random Draw
          </button>
          <button onClick={() => setShowPicker(true)} className="btn-gold">
            ✨ Select a Card
          </button>
        </div>
      </div>

      {showRandomModal && (
        <div className="modal-overlay" onClick={() => setShowRandomModal(false)}>
          <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3>Random Reading Options</h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Number of cards:</label>
              <select 
                value={randomOptions.count} 
                onChange={e => setRandomOptions({...randomOptions, count: parseInt(e.target.value)})}
                style={{ width: '100%', padding: '0.5rem' }}
              >
                <option value={-1}>🎲 Random (1-4)</option>
                {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} Card{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Arcana Type:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="arcana" 
                    checked={randomOptions.type === 'both'} 
                    onChange={() => setRandomOptions({...randomOptions, type: 'both'})}
                    style={{ width: 'auto', marginBottom: 0 }}
                  />
                  Both (Full Deck)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="arcana" 
                    checked={randomOptions.type === 'major'} 
                    onChange={() => setRandomOptions({...randomOptions, type: 'major'})}
                    style={{ width: 'auto', marginBottom: 0 }}
                  />
                  Major Arcana Only
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="arcana" 
                    checked={randomOptions.type === 'minor'} 
                    onChange={() => setRandomOptions({...randomOptions, type: 'minor'})}
                    style={{ width: 'auto', marginBottom: 0 }}
                  />
                  Minor Arcana Only
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={drawRandom} style={{ flex: 1 }}>Draw Cards</button>
              <button onClick={() => setShowRandomModal(false)} style={{ flex: 1, backgroundColor: '#666' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showPicker && (
        <div className="modal-overlay" onClick={() => setShowPicker(false)}>
          <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <h3>Select a Card</h3>
            <input 
              type="text" 
              placeholder="Search tarot cards..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
            />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
              {filteredCards.map(card => (
                <div
                  key={card.id}
                  className="card clickable-card"
                  onClick={() => addCard(card)}
                  style={{ padding: '0.5rem', textAlign: 'center' }}
                >
                  <img
                    src={getTarotImage(card.name)}
                    alt={card.name}
                    style={{ width: '100%', aspectRatio: '3/5.2', objectFit: 'cover', borderRadius: '6px', marginBottom: '0.4rem' }}
                    onError={(e: any) => { e.target.style.display = 'none'; }}
                  />
                  <strong style={{ color: 'var(--gold)', fontSize: '0.75rem' }}>{card.name}</strong>
                </div>
              ))}
            </div>
            <button onClick={() => setShowPicker(false)} style={{ marginTop: '1rem', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {selectedCards.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', opacity: 0.7 }}>
          <p style={{ fontSize: '1.2rem' }}>Your spread is empty. Click "Draw/Select a Card" to begin your reading.</p>
        </div>
      ) : (
        <div className="tarot-spread-container">
          <div className="tarot-cards-row">
            {selectedCards.map((selected) => (
              <div key={selected.instanceId} className="tarot-card-item">
                <div className={`tarot-card-visual ${selected.isReversed ? 'reversed' : ''}`}>
                   <div className="protected-image-container">
                     <div className="image-overlay" onContextMenu={(e) => e.preventDefault()}></div>
                     <img 
                       src={getTarotImage(selected.card.name)} 
                       alt={selected.card.name}
                       className="tarot-card-image no-save"
                       onError={(e: any) => { e.target.src = 'https://via.placeholder.com/300x520/1a0b2e/d4af37?text=Arcane+Circle'; }}
                     />
                   </div>
                </div>
                
                <div className="tarot-card-controls">
                  <button 
                    onClick={() => toggleOrientation(selected.instanceId)}
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                  >
                    {selected.isReversed ? '🔄 Set Upright' : '🔄 Set Reversed'}
                  </button>
                  <button 
                    onClick={() => removeCard(selected.instanceId)}
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', backgroundColor: '#8b0000' }}
                  >
                    Remove
                  </button>
                </div>

                <div className="tarot-card-meaning card">
                  <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>
                    {selected.card.name} {selected.isReversed ? '(Reversed)' : '(Upright)'}
                  </h4>
                  <div className="rich-content" style={{ fontSize: '0.9rem' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {extractMeaning(selected.card.long_description, selected.isReversed, selected.card.name)}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .tarot-spread-container {
          overflow-x: auto;
          padding-bottom: 2rem;
        }
        .tarot-cards-row {
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
          min-width: max-content;
        }
        .tarot-card-item {
          width: 280px;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .tarot-card-visual {
          width: 100%;
          aspect-ratio: 3/5.2;
          background: #0d051a;
          border: 2px solid var(--gold);
          border-radius: 12px;
          overflow: hidden;
          transition: transform 0.5s, box-shadow 0.3s;
          box-shadow: 0 10px 25px rgba(0,0,0,0.6);
          position: relative;
        }
        .tarot-card-visual.reversed {
          transform: rotate(180deg);
        }
        .tarot-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .tarot-card-controls {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }
        .tarot-card-meaning {
          max-height: 400px;
          overflow-y: auto;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          width: 90%;
          max-width: 800px;
          background: #1a1a2e;
          border: 2px solid var(--gold);
        }
        .btn-gold {
          background-color: var(--gold);
          color: black;
          font-weight: bold;
        }
        .btn-gold:hover {
          background-color: var(--pale-gold);
        }
      `}</style>
    </div>
  );
};

export default TarotReader;
